import React from "react";
import { DateTime } from "luxon";

import { usePreferences } from "@components/preferences";
import { CalendarFilter, Filter } from "@planzone/dto";
import { CalendarView } from "./displayToggle/types";
import { DisplayToggle } from "./displayToggle";
import { MonthlyView } from "./monthlyView";
import { WeeklyView } from "./weeklyView";
import { DailyView } from "./dailyView";
import { Filters } from "./filters";
import { Navigation } from "./navigation";
import { CreationMenu } from "./creationMenu";
import { Loader } from "./loader";
import { Props } from "./types";
import styles from "./styles.scss";
import { filter as f } from "@common/filter";

function Calendar({ date, ...props }: Props) {
  const userTimezone = DateTime.local().zoneName;
  const [currentView, setCurrentView] = React.useState<CalendarView>("month");
  const todayIso = (() => {
    const now = DateTime.now().setZone(userTimezone);
    return DateTime.utc(now.year, now.month, now.day).toISO() ?? "";
  })();
  const parsedDate = DateTime.fromISO(date, { zone: "utc" }).toJSDate();
  const isLoading = props.isRequestPending;
  const { state, edit } = usePreferences();
  const { calendarFilter: preferencesFilter } = state;
  const filter = props.filter ?? preferencesFilter;
  const setFilter =
    props.setFilter ??
    ((nextFilter: Filter) =>
      edit({ ...state, calendarFilter: nextFilter as CalendarFilter }));

  return (
    <div className={styles.calendar}>
      <div className={styles.actions}>
        <Filters filter={filter} setFilter={setFilter} scope={props.scope} />
        <CreationMenu project={props.project} />
      </div>
      <div className={styles.header}>
        <div className={styles.navigations}>
          <Navigation
            currentView={currentView}
            date={date}
            editDate={props.editDate}
            today={() => props.editDate(todayIso)}
          />
        </div>
        <DisplayToggle view={currentView} toggle={setCurrentView} />
      </div>
      <div className={styles.main}>
        {currentView === "month" ? (
          <MonthlyView
            date={parsedDate}
            weeks={props.month.map((week) => ({
              ...week,
              events: !isLoading
                ? f(week.events, filter, (event) => ({ event }))
                : [],
              unavailabilities: !isLoading
                ? f(week.unavailabilities, filter, (unavailability) => ({
                    unavailability,
                  }))
                : [],
              milestones: !isLoading
                ? f(week.milestones, filter, (milestone) => ({ milestone }))
                : [],
              calendarsHolidays: !isLoading
                ? f(week.calendarsHolidays, filter, (calendarHolidays) => ({
                    calendarHolidays,
                  }))
                : [],
              tasks: !isLoading
                ? f(week.tasks, filter, (task) => ({ task }))
                : [],
            }))}
            filter={filter}
          />
        ) : currentView === "week" ? (
          <WeeklyView
            date={parsedDate}
            filter={filter}
            week={props.currentWeek.week}
            events={!isLoading ? props.currentWeek.events : []}
            unavailabilities={
              !isLoading ? props.currentWeek.unavailabilities : []
            }
            milestones={!isLoading ? props.currentWeek.milestones : []}
            calendarsHolidays={
              !isLoading ? props.currentWeek.calendarsHolidays : []
            }
            tasks={!isLoading ? props.currentWeek.tasks : []}
          />
        ) : (
          <DailyView
            date={parsedDate}
            week={props.currentWeek.week}
            filter={filter}
            events={!isLoading ? props.currentWeek.events : []}
            unavailabilities={
              !isLoading ? props.currentWeek.unavailabilities : []
            }
            milestones={!isLoading ? props.currentWeek.milestones : []}
            calendarsHolidays={
              !isLoading ? props.currentWeek.calendarsHolidays : []
            }
            tasks={!isLoading ? props.currentWeek.tasks : []}
          />
        )}
        {isLoading && <Loader weeksNbr={props.month.length} />}
      </div>
    </div>
  );
}

export { Calendar };
